// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/*
 * The interface.
 *
 * It is laid out as the workflow it is — documents, then queries, then the
 * control you actually came to move — and it deliberately shows nothing that
 * looks like a score until a query has been marked. The unmarked state is not a
 * degraded one: watching ten results reorder as you drag is genuinely useful,
 * and it is honest, because nothing is being claimed. The numbers appear the
 * moment there is something real to measure them against.
 */

import './styles/main.css';

import { EventLog, stamp, type LogEntry } from './eventlog.ts';
import { mountGlossary } from './glossary.ts';
import { fromPlainText, type ExtractedDoc } from './extract.ts';
import { extractPdf } from './pdf.ts';
import { DEFAULT_MODEL, MODELS, UNSUPPORTED, modelById } from './registry.ts';
import { kFor, recommend, type AnswerSpan, type QuerySpec, type RunScore } from './score.ts';
import type { StrategyId } from './split.ts';
import type { ChunkPreview, RunRequest } from './worker.ts';
import {
  buildZip,
  chunksJsonl,
  comparisonCsv,
  download,
  reportHtml,
  splitterConfig,
  STRATEGY_LABEL,
  type ChunkRow,
} from './exports.ts';

/* ── constants ────────────────────────────────────────────────────────────── */

const SIZES = [64, 96, 128, 160, 192, 224, 256, 320, 384, 448, 512, 640, 768, 896, 1024, 1280, 1536, 2048];
const SWEEP_SIZES = [96, 128, 192, 256, 384, 512, 768, 1024];
const STRATEGIES: StrategyId[] = ['fixed', 'recursive', 'heading'];
/* How many tokens of retrieved context the user is willing to paste into their
 * model. k is derived from it, so every chunk size is judged at the same price
 * — a fixed k would hand a bigger chunk more text for free and turn "recall goes
 * up with chunk size" into arithmetic rather than a finding. */
const BUDGETS = [1024, 2048, 4096, 8192, 16_384];
/** Above this the interface warns before it starts; a phone is roughly five times slower than a laptop. */
const SOFT_TOKEN_LIMIT = 400_000;
const HARD_TOKEN_LIMIT = 2_000_000;
const SETTINGS_KEY = 'chunkforge:settings:v1';

const SAMPLE_QUERIES: { text: string; fragment: string }[] = [
  { text: 'how much notice do I need to give before taking annual leave', fragment: 'Notice of at least fourteen days' },
  { text: 'what happens to my unused study budget at the end of the year', fragment: 'Any unspent balance is forfeited' },
  { text: 'can I expense a taxi home after working late', fragment: 'the company will reimburse a door-to-door journey home' },
];

/* ── state ────────────────────────────────────────────────────────────────── */

interface Settings {
  strategy: StrategyId;
  size: number;
  overlap: number;
  modelId: string;
  /** Tokens of retrieved context per query. k = floor(budget / size). */
  budget: number;
}

interface AppState {
  docs: ExtractedDoc[];
  queries: QuerySpec[];
  selectedQuery: string | null;
  settings: Settings;
  current: RunScore | null;
  previews: ChunkPreview[];
  sweep: RunScore[];
  busy: boolean;
  ready: boolean;
  lastRunMs: number;
  boundaryLabel: string;
  totalTokens: number;
  notices: { text: string; kind: 'warn' | 'bad' | 'info' }[];
}

const log = new EventLog();
/** Per-document token counts, which only the worker knows because only it holds the tokenizer. */
const docTokens = new Map<string, number>();

const state: AppState = {
  docs: [],
  queries: [],
  selectedQuery: null,
  settings: loadSettings(),
  current: null,
  previews: [],
  sweep: [],
  busy: false,
  ready: false,
  lastRunMs: 0,
  boundaryLabel: '',
  totalTokens: 0,
  notices: [],
};

function loadSettings(): Settings {
  // 1024 with 200 overlap on the recursive splitter is the single most-copied
  // configuration in the ecosystem, so it is where chunkforge starts: whatever
  // you are running, this is probably close to it. On the shipped sample it
  // retrieves all three marked answers — and costs 4.6x more context per query
  // than the setting the sweep finds, which is a more useful thing to discover
  // than a default that was already optimal.
  const fallback: Settings = { strategy: 'recursive', size: 1024, overlap: 200, modelId: DEFAULT_MODEL, budget: 4096 };
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw) as Partial<Settings>;
    return {
      strategy: STRATEGIES.includes(parsed.strategy as StrategyId) ? (parsed.strategy as StrategyId) : fallback.strategy,
      size: SIZES.includes(parsed.size ?? 0) ? (parsed.size as number) : fallback.size,
      overlap: typeof parsed.overlap === 'number' && parsed.overlap >= 0 ? parsed.overlap : fallback.overlap,
      modelId: MODELS.some((m) => m.id === parsed.modelId) ? (parsed.modelId as string) : fallback.modelId,
      budget: BUDGETS.includes(parsed.budget ?? 0) ? (parsed.budget as number) : fallback.budget,
    };
  } catch {
    return fallback;
  }
}

function saveSettings(): void {
  try {
    // Settings only. Document text and query text are never persisted — the
    // questions someone types are often more revealing than the documents.
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(state.settings));
  } catch {
    /* private browsing, a full quota — neither is a reason to fail */
  }
}

/* ── worker ───────────────────────────────────────────────────────────────── */

const worker = new Worker(new URL('./worker.ts', import.meta.url), { type: 'module' });

let runToken = 0;
let pendingRun: number | null = null;

worker.onmessage = (event: MessageEvent<Record<string, unknown>>) => {
  const msg = event.data;
  switch (msg.t) {
    case 'progress': {
      const received = msg.received as number;
      const total = msg.total as number;
      setProgress(`${msg.stage as string}`, total > 0 ? received / total : 0, msg.fromCache as boolean);
      break;
    }
    case 'ready': {
      state.ready = true;
      state.boundaryLabel = msg.boundary as string;
      clearProgress();
      log.add(`Scoring model ready — ${msg.vocab} rows at ${msg.dims} dimensions`, 'good');
      log.add(`Counting chunk size in: ${msg.boundary}`);
      render();
      break;
    }
    case 'modelSet': {
      state.boundaryLabel = msg.label as string;
      log.add(`Now counting in ${msg.label as string} tokens`, 'good');
      break;
    }
    case 'retokenized': {
      state.totalTokens = msg.totalTokens as number;
      requestRun();
      break;
    }
    case 'tokenizerFallback': {
      state.notices.push({
        text: `${msg.model as string}: ${msg.reason as string}. Chunk sizes are being counted with a labelled approximation rather than that model's tokenizer — treat the number as indicative, not exact.`,
        kind: 'warn',
      });
      log.add(`Tokenizer unavailable for ${msg.model as string}: ${msg.reason as string}`, 'warn');
      render();
      break;
    }
    case 'indexed': {
      state.totalTokens = msg.totalTokens as number;
      const docs = msg.docs as { name: string; tokens: number; pages: number; headings: number; runningLinesDropped: number; lineOrder: string; hasTextLayer: boolean }[];
      for (const d of docs) {
        docTokens.set(d.name, d.tokens);
        log.add(
          `${d.name}: ${d.tokens.toLocaleString()} tokens, ${d.pages} page${d.pages === 1 ? '' : 's'}, ${d.headings} headings${d.runningLinesDropped ? `, ${d.runningLinesDropped} running header lines removed` : ''}${d.lineOrder === 'geometric' ? ', lines reordered by geometry' : ''}`,
          d.hasTextLayer ? 'good' : 'warn',
        );
      }
      clearProgress();
      if (state.totalTokens > SOFT_TOKEN_LIMIT) {
        state.notices.push({
          text: `This corpus is ${state.totalTokens.toLocaleString()} tokens. Every slider move re-chunks and re-embeds all of it, so expect the controls to lag on a phone. A sweep across all sizes will take noticeably longer.`,
          kind: 'warn',
        });
      }
      requestRun();
      render();
      break;
    }
    case 'result': {
      state.current = msg.score as RunScore;
      state.previews = msg.previews as ChunkPreview[];
      state.lastRunMs = msg.ms as number;
      state.busy = false;
      render();
      break;
    }
    case 'sweepProgress': {
      setProgress(`comparing settings`, (msg.done as number) / (msg.total as number), false);
      break;
    }
    case 'sweep': {
      state.sweep = msg.scores as RunScore[];
      state.busy = false;
      clearProgress();
      log.add(`Compared ${state.sweep.length} settings`, 'good');
      render();
      break;
    }
    case 'exported': {
      finishExport(msg.rows as ChunkRow[], msg.score as RunScore);
      break;
    }
    case 'error': {
      state.busy = false;
      clearProgress();
      state.notices.push({ text: msg.message as string, kind: 'bad' });
      log.add(`Error: ${msg.message as string}`, 'bad');
      render();
      break;
    }
  }
};

function requestRun(): void {
  if (!state.ready || state.docs.length === 0) return;
  const token = ++runToken;
  if (pendingRun !== null) window.clearTimeout(pendingRun);
  pendingRun = window.setTimeout(() => {
    if (token !== runToken) return;
    state.busy = true;
    worker.postMessage({
      t: 'run',
      run: currentRun(),
      queries: state.queries,
      budget: state.settings.budget,
      previewFor: state.selectedQuery,
    });
  }, 90);
}

function currentRun(): RunRequest {
  return { strategy: state.settings.strategy, size: state.settings.size, overlap: state.settings.overlap };
}

/* ── ingestion ────────────────────────────────────────────────────────────── */

async function ingest(files: File[]): Promise<void> {
  if (files.length === 0) return;
  state.notices = [];
  const added: ExtractedDoc[] = [];
  for (const file of files) {
    try {
      log.add(`Reading ${file.name} (${formatBytes(file.size)})`);
      setProgress(`reading ${file.name}`, 0, false);
      const buf = await file.arrayBuffer();
      let doc: ExtractedDoc;
      const lower = file.name.toLowerCase();
      if (lower.endsWith('.pdf')) {
        const result = await extractPdf(buf, file.name, (p) =>
          setProgress(`${file.name} — page ${p.page} of ${p.pages}`, p.page / p.pages, false),
        );
        doc = result;
        if (result.emptyPages > 0 && result.hasTextLayer) {
          // A partly-scanned document is a hole in every number computed over it,
          // and it is invisible unless someone says so: pdf.js returns an empty
          // page without an error.
          state.notices.push({
            text: `${file.name}: ${result.emptyPages} of ${result.pages.length} pages produced no extractable text — they are probably scanned images. Anything in them cannot be retrieved and is not counted.`,
            kind: 'warn',
          });
        }
      } else {
        const text = new TextDecoder().decode(new Uint8Array(buf));
        doc = fromPlainText(file.name, text, lower.endsWith('.md') || lower.endsWith('.markdown'));
      }
      if (!doc.hasTextLayer) {
        state.notices.push({
          text: `${file.name} has no text layer — it is a picture of words. chunkforge cannot chunk it, and it is left out rather than counted as an empty document.`,
          kind: 'warn',
        });
        log.add(`${file.name}: no text layer, skipped`, 'warn');
        continue;
      }
      added.push(doc);
    } catch (err) {
      state.notices.push({ text: `${file.name}: ${(err as Error).message}`, kind: 'bad' });
      log.add(`${file.name} failed: ${(err as Error).message}`, 'bad');
    }
  }
  if (added.length === 0) {
    clearProgress();
    render();
    return;
  }
  state.docs = [...state.docs, ...added];
  const rough = state.docs.reduce((n, d) => n + d.text.length, 0) / 3.6;
  if (rough > HARD_TOKEN_LIMIT) {
    state.notices.push({
      text: `That is more text than chunkforge will hold in one tab (roughly ${Math.round(rough / 1000)}k tokens against a ceiling of ${HARD_TOKEN_LIMIT / 1000}k). Chunk size is a property of your documents, not your whole corpus — a representative few hundred pages answers the same question.`,
      kind: 'bad',
    });
    state.docs = state.docs.slice(0, state.docs.length - added.length);
    clearProgress();
    render();
    return;
  }
  sendDocs();
  render();
}

function sendDocs(): void {
  setProgress('indexing', 0, false);
  worker.postMessage({
    t: 'docs',
    docs: state.docs.map((d) => ({
      name: d.name,
      text: d.text,
      pages: d.pages,
      paragraphs: d.paragraphs,
      headings: d.headings,
      runningLinesDropped: d.runningLinesDropped,
      lineOrder: d.lineOrder,
      hasTextLayer: d.hasTextLayer,
    })),
  });
}

async function loadSample(): Promise<void> {
  try {
    log.add('Loading the sample handbook');
    const res = await fetch('samples/handbook.pdf');
    if (!res.ok) throw new Error(`sample unavailable (HTTP ${res.status})`);
    const blob = await res.blob();
    const file = new File([blob], 'handbook.pdf', { type: 'application/pdf' });
    state.docs = [];
    state.queries = [];
    // Straight through the real ingestion path — the same function a dropped
    // file goes through, so the demo cannot be right while the product is wrong.
    await ingest([file]);
    const doc = state.docs[0];
    if (!doc) return;
    state.queries = SAMPLE_QUERIES.map((q, i) => {
      const span = spanForFragment(doc, q.fragment, 0);
      return { id: `q${i}`, text: q.text, spans: span ? [span] : [] };
    });
    state.selectedQuery = state.queries[0]?.id ?? null;
    const marked = state.queries.filter((q) => q.spans.length > 0).length;
    log.add(`Sample loaded with ${marked} of ${state.queries.length} answers pre-marked`, marked === 3 ? 'good' : 'warn');
    requestRun();
    render();
  } catch (err) {
    state.notices.push({ text: `Could not load the sample: ${(err as Error).message}`, kind: 'bad' });
    render();
  }
}

/**
 * Locate a pre-marked answer by its text rather than by a stored offset.
 *
 * Offsets into an extracted PDF depend on the extraction, and the extraction is
 * part of what this tool is; hard-coding them would make the sample silently
 * wrong the first time the layout engine improved. Searching for the sentence
 * and growing it to its boundaries survives that, and fails visibly rather than
 * pointing at the wrong passage.
 */
function spanForFragment(doc: ExtractedDoc, fragment: string, docId: number): AnswerSpan | null {
  const at = doc.text.indexOf(fragment);
  if (at < 0) return null;
  let start = at;
  while (start > 0 && !/[.!?\n]/.test(doc.text[start - 1])) start -= 1;
  while (start < doc.text.length && /\s/.test(doc.text[start])) start += 1;
  let end = at + fragment.length;
  while (end < doc.text.length && !/[.!?\n]/.test(doc.text[end])) end += 1;
  if (end < doc.text.length) end += 1;
  return { docId, start, end };
}

/* ── rendering ────────────────────────────────────────────────────────────── */

const app = document.getElementById('app') as HTMLElement;

function el<K extends keyof HTMLElementTagNameMap>(tag: K, className?: string, text?: string): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function glossaryTerm(term: string, label: string): HTMLSpanElement {
  const span = el('span', 'glossary-link', label);
  span.dataset.term = term;
  span.tabIndex = 0;
  return span;
}

let progressHost: HTMLDivElement | null = null;

function setProgress(label: string, fraction: number, fromCache: boolean): void {
  if (!progressHost) return;
  progressHost.hidden = false;
  const text = progressHost.querySelector('.progress-label span:first-child') as HTMLElement | null;
  const pct = progressHost.querySelector('.progress-label span:last-child') as HTMLElement | null;
  const fill = progressHost.querySelector('.progress-fill') as HTMLElement | null;
  if (text) text.textContent = fromCache ? `${label} (from cache)` : label;
  if (pct) pct.textContent = `${Math.round(Math.min(1, Math.max(0, fraction)) * 100)}%`;
  if (fill) fill.style.width = `${Math.min(1, Math.max(0, fraction)) * 100}%`;
}

function clearProgress(): void {
  if (progressHost) progressHost.hidden = true;
}

function render(): void {
  app.replaceChildren(header(), mainContent(), footer(), logToggle());
}

function header(): HTMLElement {
  const head = el('header', 'site-header');
  const inner = el('div', 'header-inner');

  const mark = el('div', 'wordmark');
  mark.innerHTML =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/><circle cx="15" cy="6" r="2.2" fill="currentColor" stroke="none"/><circle cx="8" cy="12" r="2.2" fill="currentColor" stroke="none"/><circle cx="17" cy="18" r="2.2" fill="currentColor" stroke="none"/></svg>';
  mark.append(el('span', undefined, 'chunkforge'));
  inner.append(mark);
  inner.append(el('span', 'header-tag', 'find the chunk size your retrieval actually needs'));

  const actions = el('div', 'header-actions');
  const how = el('button', 'btn', 'How it works');
  how.addEventListener('click', () => openModal('How chunkforge works', 'tpl-how'));
  const privacy = el('button', 'btn', 'Privacy');
  privacy.addEventListener('click', () => openModal('Privacy & threat model', 'tpl-privacy'));
  const about = el('button', 'btn', 'About');
  about.addEventListener('click', () => openModal('About chunkforge', 'tpl-about'));
  actions.append(how, privacy, about);
  inner.append(actions);
  head.append(inner);
  return head;
}

function footer(): HTMLElement {
  const foot = el('footer', 'site-footer');
  const inner = el('div', 'footer-inner');
  inner.innerHTML =
    'Built by <a href="https://benrichardson.dev/" target="_blank" rel="noopener">benrichardson.dev</a> · <a href="https://lab.benrichardson.dev" target="_blank" rel="noopener">more tools &amp; sites</a>';
  foot.append(inner);
  return foot;
}

function mainContent(): HTMLElement {
  const main = el('main', 'main-content');

  const trust = el('button', 'trust');
  trust.type = 'button';
  trust.innerHTML =
    '<strong>Runs entirely in your browser.</strong> Your documents and the questions you type never leave this device. What that does and does not cover →';
  trust.addEventListener('click', () => openModal('Privacy & threat model', 'tpl-privacy'));
  main.append(trust);

  progressHost = el('div', 'progress');
  progressHost.hidden = true;
  const plabel = el('div', 'progress-label');
  plabel.append(el('span', undefined, 'working'), el('span', undefined, '0%'));
  const bar = el('div', 'progress-bar');
  bar.append(el('div', 'progress-fill'));
  progressHost.append(plabel, bar);
  main.append(progressHost);

  for (const n of state.notices) {
    const notice = el('div', `notice${n.kind === 'bad' ? ' is-bad' : n.kind === 'info' ? ' is-info' : ''}`, n.text);
    main.append(notice);
  }

  main.append(stepDocuments());
  if (state.docs.length > 0) {
    main.append(stepQueries());
    main.append(stepControls());
    main.append(stepResults());
  }
  return main;
}

function stepHead(num: string, title: string, note?: string): HTMLElement {
  const head = el('div', 'step-head');
  head.append(el('span', 'step-num', num), el('h2', 'step-title', title));
  if (note) head.append(el('span', 'step-note', note));
  return head;
}

function stepDocuments(): HTMLElement {
  const step = el('section', 'step');
  step.append(stepHead('1', 'Your documents', 'PDF, Markdown or plain text — parsed here, never uploaded'));

  const zone = el('div', 'dropzone');
  zone.tabIndex = 0;
  zone.setAttribute('role', 'button');
  zone.append(el('div', 'dropzone-title', state.docs.length ? 'Add more documents' : 'Drop documents here'));
  zone.append(el('div', 'dropzone-sub', 'or tap to choose  ·  .pdf, .md, .txt'));

  const input = el('input');
  input.type = 'file';
  input.multiple = true;
  input.accept = '.pdf,.md,.markdown,.txt,text/plain,application/pdf';
  input.hidden = true;
  input.addEventListener('change', () => {
    void ingest(Array.from(input.files ?? []));
    input.value = '';
  });

  zone.addEventListener('click', () => input.click());
  zone.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      input.click();
    }
  });
  zone.addEventListener('dragover', (e) => {
    e.preventDefault();
    zone.classList.add('is-over');
  });
  zone.addEventListener('dragleave', () => zone.classList.remove('is-over'));
  zone.addEventListener('drop', (e) => {
    e.preventDefault();
    zone.classList.remove('is-over');
    void ingest(Array.from(e.dataTransfer?.files ?? []));
  });

  const sampleRow = el('div', 'sample-row');
  const sample = el('button', 'pill', 'Try the sample handbook');
  sample.type = 'button';
  sample.addEventListener('click', (e) => {
    // Inside the drop zone, so the click must not also open the file picker.
    e.stopPropagation();
    void loadSample();
  });
  sampleRow.append(sample);
  zone.append(sampleRow);

  step.append(zone, input);

  if (state.docs.length > 0) {
    const list = el('div', 'doclist');
    for (const doc of state.docs) {
      const row = el('div', 'docrow');
      row.append(el('span', 'docrow-name', doc.name));
      const bits = [
        `${doc.pages.length} page${doc.pages.length === 1 ? '' : 's'}`,
        `${doc.headings.length} headings`,
        `${doc.paragraphs.length} paragraphs`,
      ];
      if (doc.runningLinesDropped > 0) bits.push(`${doc.runningLinesDropped} running lines removed`);
      if (doc.lineOrder === 'geometric') bits.push('reordered by geometry');
      row.append(el('span', 'docrow-meta', bits.join('  ·  ')));
      const remove = el('button', 'btn btn-ghost', '✕');
      remove.setAttribute('aria-label', `Remove ${doc.name}`);
      remove.addEventListener('click', () => {
        state.docs = state.docs.filter((d) => d !== doc);
        state.queries = state.queries.map((q) => ({ ...q, spans: [] }));
        if (state.docs.length > 0) sendDocs();
        else {
          state.current = null;
          state.previews = [];
          state.sweep = [];
        }
        render();
      });
      row.append(remove);
      list.append(row);
    }
    step.append(list);
    if (state.totalTokens > 0) {
      const total = el('div', 'step-note', `${state.totalTokens.toLocaleString()} tokens in total, counted with ${state.boundaryLabel}`);
      total.style.marginTop = 'var(--space-sm)';
      step.append(total);
    }
  }
  return step;
}

function stepQueries(): HTMLElement {
  const step = el('section', 'step');
  step.append(stepHead('2', 'The queries that are failing', 'and the passage each one should have found'));

  const list = el('div', 'queries');
  for (const q of state.queries) {
    const row = el('div', `query${state.selectedQuery === q.id ? ' is-selected' : ''}`);
    const top = el('div', 'query-top');

    const input = el('input');
    input.type = 'text';
    input.value = q.text;
    input.placeholder = 'a real question your users ask that comes back wrong';
    input.addEventListener('input', () => {
      q.text = input.value;
      requestRun();
    });
    input.addEventListener('focus', () => {
      state.selectedQuery = q.id;
      requestRun();
      render();
    });
    top.append(input);

    const markBtn = el('button', 'btn', q.spans.length ? 'Change the marked passage' : 'Mark the answer');
    markBtn.addEventListener('click', () => openReader(q));
    top.append(markBtn);

    const del = el('button', 'btn btn-ghost', '✕');
    del.setAttribute('aria-label', 'Remove this query');
    del.addEventListener('click', () => {
      state.queries = state.queries.filter((x) => x.id !== q.id);
      if (state.selectedQuery === q.id) state.selectedQuery = state.queries[0]?.id ?? null;
      requestRun();
      render();
    });
    top.append(del);
    row.append(top);

    const status = el('div', 'query-status');
    if (q.spans.length === 0) {
      const warn = el('span', 'tagline-mark', '⃝ not marked — nothing can be scored for this query yet');
      status.append(warn);
    } else {
      const doc = state.docs[q.spans[0].docId];
      const text = doc ? doc.text.slice(q.spans[0].start, q.spans[0].end) : '';
      status.append(el('span', 'tagline-ok', '✓ marked'));
      status.append(el('span', undefined, `“${text.slice(0, 110)}${text.length > 110 ? '…' : ''}”`));
    }
    const score = state.current?.perQuery.find((p) => p.queryId === q.id);
    if (score && score.marked) {
      status.append(
        el(
          'span',
          undefined,
          score.retrieved
            ? `retrieved · first hit ${score.firstHitRank} of ${score.chunkCount} chunks`
            : `not retrieved in the top ${state.current?.k ?? 0} of ${score.chunkCount} chunks`,
        ),
      );
    }
    row.append(status);
    list.append(row);
  }
  step.append(list);

  const add = el('button', 'btn', '+ Add a query');
  add.style.marginTop = 'var(--space-md)';
  add.disabled = state.queries.length >= 8;
  add.addEventListener('click', () => {
    const id = `q${Date.now().toString(36)}`;
    state.queries.push({ id, text: '', spans: [] });
    state.selectedQuery = id;
    render();
  });
  step.append(add);
  return step;
}

function stepControls(): HTMLElement {
  const step = el('section', 'step');
  step.append(stepHead('3', 'The controls', 'every change re-chunks, re-embeds and re-ranks everything'));

  const box = el('div', 'controls');

  // strategy
  const stratWrap = el('div', 'control');
  const stratLabel = el('div', 'control-label');
  stratLabel.append(glossaryTerm('strategy', 'Strategy'));
  stratWrap.append(stratLabel);
  const seg = el('div', 'segmented');
  for (const s of STRATEGIES) {
    const b = el('button', undefined, s === 'fixed' ? 'Fixed' : s === 'recursive' ? 'Recursive' : 'Heading');
    b.setAttribute('aria-pressed', String(state.settings.strategy === s));
    b.addEventListener('click', () => {
      state.settings.strategy = s;
      saveSettings();
      requestRun();
      render();
    });
    seg.append(b);
  }
  stratWrap.append(seg);
  box.append(stratWrap);

  // size
  const sizeWrap = el('div', 'control');
  const sizeLabel = el('label');
  sizeLabel.append(document.createTextNode('Chunk size'));
  sizeWrap.append(sizeLabel);
  const sizeValue = el('div', 'value-badge', `${state.settings.size} tokens`);
  sizeWrap.append(sizeValue);
  const sizeRange = el('input');
  sizeRange.type = 'range';
  sizeRange.min = '0';
  sizeRange.max = String(SIZES.length - 1);
  sizeRange.step = '1';
  sizeRange.value = String(Math.max(0, SIZES.indexOf(state.settings.size)));
  sizeRange.addEventListener('input', () => {
    state.settings.size = SIZES[Number(sizeRange.value)];
    sizeValue.textContent = `${state.settings.size} tokens`;
    if (state.settings.overlap >= state.settings.size) state.settings.overlap = 0;
    saveSettings();
    requestRun();
  });
  sizeWrap.append(sizeRange);
  const model = modelById(state.settings.modelId);
  if (state.settings.size > model.maxTokens) {
    sizeWrap.append(el('div', 'value-sub', `above ${model.label}'s ${model.maxTokens}-token limit — the tail gets truncated silently`));
  }
  box.append(sizeWrap);

  // overlap
  const overlapWrap = el('div', 'control');
  const overlapLabel = el('div', 'control-label');
  overlapLabel.append(glossaryTerm('overlap', 'Overlap'));
  overlapWrap.append(overlapLabel);
  const overlapValue = el('div', 'value-badge', `${state.settings.overlap} tokens`);
  overlapWrap.append(overlapValue);
  const overlapRange = el('input');
  overlapRange.type = 'range';
  overlapRange.min = '0';
  overlapRange.max = '50';
  overlapRange.step = '5';
  overlapRange.value = String(Math.round((state.settings.overlap / state.settings.size) * 100));
  overlapRange.addEventListener('input', () => {
    state.settings.overlap = Math.round((Number(overlapRange.value) / 100) * state.settings.size);
    overlapValue.textContent = `${state.settings.overlap} tokens`;
    saveSettings();
    requestRun();
  });
  overlapWrap.append(overlapRange);
  overlapWrap.append(el('div', 'value-sub', `${overlapRange.value}% of the chunk`));
  if (state.settings.overlap >= state.settings.size * 0.5) {
    overlapWrap.append(
      el(
        'div',
        'value-sub',
        'At half the chunk size or more, every token is stored two or three times and the chunk count multiplies. LangChain will do exactly this if you ask it to.',
      ),
    );
  }
  box.append(overlapWrap);

  // context budget — the control that makes every other number comparable
  const budgetWrap = el('div', 'control');
  const budgetLabel = el('div', 'control-label');
  budgetLabel.append(glossaryTerm('budget', 'Context budget'));
  budgetWrap.append(budgetLabel);
  const budgetSelect = el('select');
  for (const b of BUDGETS) {
    const opt = el('option', undefined, `${b.toLocaleString()} tokens`);
    opt.value = String(b);
    if (b === state.settings.budget) opt.selected = true;
    budgetSelect.append(opt);
  }
  budgetSelect.addEventListener('change', () => {
    state.settings.budget = Number(budgetSelect.value);
    saveSettings();
    state.sweep = [];
    requestRun();
    render();
  });
  budgetWrap.append(budgetSelect);
  budgetWrap.append(
    el(
      'div',
      'value-sub',
      `retrieves ${kFor(state.settings.budget, state.settings.size)} chunk${kFor(state.settings.budget, state.settings.size) === 1 ? '' : 's'} at this size — every size is judged at the same price`,
    ),
  );
  box.append(budgetWrap);

  // model
  const modelWrap = el('div', 'control');
  const modelLabel = el('div', 'control-label');
  modelLabel.append(document.createTextNode('Counting in '));
  modelLabel.append(glossaryTerm('tokenizer', "your model's tokens"));
  modelWrap.append(modelLabel);
  const select = el('select');
  for (const m of MODELS) {
    const opt = el('option', undefined, m.label);
    opt.value = m.id;
    if (m.id === state.settings.modelId) opt.selected = true;
    select.append(opt);
  }
  select.addEventListener('change', () => {
    state.settings.modelId = select.value;
    saveSettings();
    setProgress('loading tokenizer', 0, false);
    worker.postMessage({ t: 'setModel', modelId: select.value });
    render();
  });
  modelWrap.append(select);
  const note = modelById(state.settings.modelId).note;
  if (note) modelWrap.append(el('div', 'value-sub', note));
  box.append(modelWrap);

  step.append(box);

  const unsupported = el('details');
  unsupported.style.marginTop = 'var(--space-md)';
  const summary = el('summary', 'step-note', 'Models chunkforge will not count for, and why');
  summary.style.cursor = 'pointer';
  unsupported.append(summary);
  const ul = el('ul');
  ul.style.color = 'var(--text-tertiary)';
  ul.style.fontSize = 'var(--font-size-sm)';
  for (const u of UNSUPPORTED) {
    const li = el('li');
    li.append(el('strong', undefined, u.label + ' — '), document.createTextNode(u.reason));
    ul.append(li);
  }
  unsupported.append(ul);
  step.append(unsupported);
  return step;
}

function headline(): { text: string; sub: string } {
  const run = state.current;
  if (!run) return { text: 'Loading…', sub: '' };
  const marked = run.markedCount;
  if (marked === 0) {
    return {
      text: 'Mark the passage each query should have found, and these results become a measurement.',
      sub: 'Until then you are watching the ranking move, which is useful — but no score is being claimed, because without ground truth there is nothing to score against.',
    };
  }
  const settingLabel = `${STRATEGY_LABEL[run.strategy as StrategyId]} at ${run.size} tokens${run.overlap ? ` with ${run.overlap} overlap` : ''}`;
  if (run.degenerate) {
    return {
      text: `At ${run.size} tokens, ${run.k} results is half your whole corpus.`,
      sub: `Anything would score well here, so nothing is being claimed. Lower the context budget or raise the chunk size until k is a small fraction of the ${run.chunkCount.toLocaleString()} chunks.`,
    };
  }
  const cost = Math.round(run.meanRetrievedTokens).toLocaleString();
  if (run.retrievedCount === marked) {
    return {
      text: `${settingLabel} returns ${marked} of your ${marked} marked passage${marked === 1 ? '' : 's'} in the top ${run.k}.`,
      sub: `${run.chunkCount.toLocaleString()} chunks, median ${run.medianTokens} tokens, about ${cost} retrieved tokens per query. Random selection would score ${(run.chanceLevel * 100).toFixed(0)}% here.`,
    };
  }
  return {
    text: `${settingLabel} returns ${run.retrievedCount} of your ${marked} marked passage${marked === 1 ? '' : 's'} in the top ${run.k}.`,
    sub: `${run.chunkCount.toLocaleString()} chunks, about ${cost} retrieved tokens per query. Random selection would score ${(run.chanceLevel * 100).toFixed(0)}%. Compare every setting below before changing anything else.`,
  };
}

function stepResults(): HTMLElement {
  const step = el('section', 'step');
  step.append(stepHead('4', 'What comes back', state.lastRunMs ? `recomputed in ${state.lastRunMs} ms` : undefined));

  const board = el('div', 'scoreboard');
  const h = headline();
  const headlineEl = el('p', 'headline');
  headlineEl.textContent = h.text;
  board.append(headlineEl);
  board.append(el('p', 'headline-sub', h.sub));

  const run = state.current;
  if (run && run.degenerate) {
    board.append(
      el(
        'div',
        'notice',
        `The top ${run.k} results are ${Math.round((run.k / run.chunkCount) * 100)}% of your whole corpus at this chunk size, so a good score here is arithmetic rather than retrieval. The comparison is not meaningful until k is a small fraction of the ${run.chunkCount.toLocaleString()} chunks.`,
      ),
    );
  }
  if (run && run.markedCount > 0) {
    const stats = el('div', 'stat-row');
    const add = (labelNode: Node, value: string, cls = '') => {
      const cell = el('div');
      const lab = el('div', 'stat-label');
      lab.append(labelNode);
      cell.append(lab, el('div', `stat-value ${cls}`.trim(), value));
      stats.append(cell);
    };
    add(
      glossaryTerm('hit', 'Answers retrieved'),
      `${run.retrievedCount} of ${run.markedCount}`,
      run.retrievedCount === run.markedCount ? 'is-good' : run.retrievedCount > 0 ? 'is-warn' : '',
    );
    add(glossaryTerm('chance', 'Chance level'), `${(run.chanceLevel * 100).toFixed(0)}%`, run.chanceLevel > 0.3 ? 'is-warn' : '');
    add(glossaryTerm('retrieved', 'Retrieved tokens'), Math.round(run.meanRetrievedTokens).toLocaleString());
    add(document.createTextNode('Results (k)'), `${run.k} of ${run.chunkCount.toLocaleString()}`);
    if (run.oversized > 0) add(document.createTextNode('Over the size limit'), String(run.oversized), 'is-warn');
    board.append(stats);
  }
  step.append(board);

  // query selector for the preview
  if (state.queries.length > 1) {
    const seg = el('div', 'segmented');
    seg.style.marginBottom = 'var(--space-md)';
    for (const q of state.queries) {
      const b = el('button', undefined, q.text.slice(0, 28) || 'untitled');
      b.setAttribute('aria-pressed', String(state.selectedQuery === q.id));
      b.addEventListener('click', () => {
        state.selectedQuery = q.id;
        requestRun();
        render();
      });
      seg.append(b);
    }
    step.append(seg);
  }

  const results = el('div', 'results');
  if (state.previews.length === 0) {
    results.append(el('div', 'notice is-info', state.busy ? 'Working…' : 'Type a query above to see what comes back.'));
  }
  for (let i = 0; i < state.previews.length; i++) {
    const p = state.previews[i];
    const hit = p.isHit;
    const card = el('div', `result${hit ? ' is-hit' : ''}`);
    const head = el('div', 'result-head');
    head.append(el('span', 'result-rank', `#${i + 1}`));
    head.append(el('span', undefined, `score ${p.score.toFixed(3)}`));
    head.append(el('span', undefined, `${p.tokenCount} tokens`));
    head.append(el('span', undefined, `${p.docName} p.${p.page}`));
    if (hit) head.append(el('span', 'result-hit-flag', '✓ contains the marked passage'));
    card.append(head);
    if (p.prefix) card.append(el('span', 'result-prefix', p.prefix.replace(/\n$/, '')));
    const body = el('div', 'result-text');
    appendWithHighlight(body, p);
    card.append(body);
    results.append(card);
  }
  step.append(results);

  step.append(sweepSection());
  step.append(exportSection());
  return step;
}

/** Highlight the marked passage inside a preview without ever using innerHTML. */
function appendWithHighlight(host: HTMLElement, p: ChunkPreview): void {
  const query = state.queries.find((q) => q.id === state.selectedQuery);
  const span = query?.spans.find((s) => s.docId === p.docId && s.start < p.end && s.end > p.start);
  const text = p.snippet;
  if (!span) {
    host.textContent = text;
    return;
  }
  const from = Math.max(0, span.start - p.start);
  const to = Math.min(text.length, span.end - p.start);
  if (to <= from) {
    host.textContent = text;
    return;
  }
  host.append(document.createTextNode(text.slice(0, from)));
  const mark = el('mark', undefined, text.slice(from, to));
  host.append(mark, document.createTextNode(text.slice(to)));
}

function sweepSection(): HTMLElement {
  const wrap = el('div');
  wrap.style.marginTop = 'var(--space-xl)';

  const bar = el('div', 'export-row');
  const go = el('button', 'btn btn-primary', 'Compare every size and strategy');
  go.disabled = state.busy || state.queries.every((q) => q.spans.length === 0);
  go.addEventListener('click', () => {
    // The overlap is held as a RATIO across the sweep, not as an absolute token
    // count. Carrying 200 tokens of overlap down to a 96-token chunk clamps it to
    // 95 and makes the stride ONE TOKEN: measured, 11,014 chunks from an
    // 11,000-token document, which is not a setting anybody would run and which
    // crowded out every real row in the table.
    const ratio = Math.min(0.9, state.settings.overlap / Math.max(1, state.settings.size));
    const runs: RunRequest[] = [];
    for (const s of STRATEGIES) {
      for (const size of SWEEP_SIZES) runs.push({ strategy: s, size, overlap: Math.round(ratio * size) });
    }
    state.busy = true;
    setProgress('comparing settings', 0, false);
    log.add(`Comparing ${runs.length} settings`);
    worker.postMessage({ t: 'sweep', runs, queries: state.queries, budget: state.settings.budget });
    render();
  });
  bar.append(go);
  if (state.queries.every((q) => q.spans.length === 0)) {
    bar.append(el('span', 'step-note', 'Mark at least one answer first — a comparison needs something to compare against.'));
  }
  wrap.append(bar);

  if (state.sweep.length === 0) return wrap;

  const { best, complete } = recommend(state.sweep);
  if (best) {
    const rec = el('div', `notice ${complete ? 'is-info' : ''}`.trim());
    rec.textContent = complete
      ? `Cheapest setting that returns everything you marked: ${STRATEGY_LABEL[best.strategy as StrategyId]} at ${best.size} tokens — ${Math.round(best.meanRetrievedTokens).toLocaleString()} retrieved tokens per query, ${best.chunkCount.toLocaleString()} chunks.`
      : `Nothing tried returns every marked passage. The best was ${STRATEGY_LABEL[best.strategy as StrategyId]} at ${best.size} tokens, which returned ${best.retrievedCount} of ${best.markedCount}. That usually means the passage is worded very differently from the query — check the marked text, and try a query closer to how a user would actually ask it.`;
    wrap.append(rec);
    const use = el('button', 'btn');
    use.textContent = 'Use that setting';
    use.addEventListener('click', () => {
      state.settings.strategy = best.strategy as StrategyId;
      state.settings.size = best.size;
      saveSettings();
      requestRun();
      render();
    });
    wrap.append(use);
  }

  const tableWrap = el('div', 'table-wrap');
  tableWrap.style.marginTop = 'var(--space-md)';
  const table = el('table');
  const thead = el('thead');
  const hr = el('tr');
  for (const h of ['Strategy', 'Size', 'Chunks', 'Median tokens', 'Results (k)', 'Answers retrieved', 'Retrieved tokens', 'Chance level', 'Oversized']) {
    hr.append(el('th', undefined, h));
  }
  thead.append(hr);
  table.append(thead);
  const tbody = el('tbody');
  for (const s of state.sweep) {
    const tr = el('tr');
    if (best && s === best) tr.classList.add('is-best');
    if (s.strategy === state.settings.strategy && s.size === state.settings.size) tr.classList.add('is-current');
    tr.append(el('td', undefined, STRATEGY_LABEL[s.strategy as StrategyId]));
    tr.append(el('td', undefined, String(s.size)));
    tr.append(el('td', undefined, s.chunkCount.toLocaleString()));
    tr.append(el('td', undefined, String(s.medianTokens)));
    tr.append(el('td', undefined, String(s.k)));
    tr.append(el('td', undefined, `${s.retrievedCount} of ${s.markedCount}`));
    tr.append(el('td', undefined, Math.round(s.meanRetrievedTokens).toLocaleString()));
    tr.append(el('td', undefined, `${(s.chanceLevel * 100).toFixed(0)}%`));
    tr.append(el('td', undefined, String(s.oversized)));
    tbody.append(tr);
  }
  table.append(tbody);
  tableWrap.append(table);
  wrap.append(tableWrap);
  return wrap;
}

function exportSection(): HTMLElement {
  const wrap = el('div');
  wrap.style.marginTop = 'var(--space-xl)';
  wrap.append(el('h3', 'step-title', 'Take it with you'));

  const row = el('div', 'export-row');
  const go = el('button', 'btn btn-primary', 'Download chunks, config and report');
  go.disabled = state.busy || state.docs.length === 0;
  go.addEventListener('click', () => {
    state.busy = true;
    setProgress('building the export', 0.4, false);
    worker.postMessage({ t: 'export', run: currentRun(), queries: state.queries, budget: state.settings.budget });
    render();
  });
  row.append(go);

  const copy = el('button', 'btn', 'Copy the splitter config');
  copy.addEventListener('click', () => {
    const run = state.current;
    const text = splitterConfig({
      strategy: state.settings.strategy,
      size: state.settings.size,
      overlap: state.settings.overlap,
      model: modelById(state.settings.modelId),
      chunkCount: run?.chunkCount ?? 0,
      oversized: run?.oversized ?? 0,
      medianTokens: run?.medianTokens ?? 0,
      budget: state.settings.budget,
      topK: kFor(state.settings.budget, state.settings.size),
    });
    void navigator.clipboard?.writeText(text).then(
      () => {
        copy.textContent = 'Copied';
        log.add('Splitter config copied to the clipboard', 'good');
        setTimeout(() => (copy.textContent = 'Copy the splitter config'), 1800);
      },
      () => {
        copy.textContent = 'Clipboard blocked';
        setTimeout(() => (copy.textContent = 'Copy the splitter config'), 1800);
      },
    );
  });
  row.append(copy);
  wrap.append(row);

  const run = state.current;
  const preview = el('pre', 'snippet');
  preview.textContent = splitterConfig({
    strategy: state.settings.strategy,
    size: state.settings.size,
    overlap: state.settings.overlap,
    model: modelById(state.settings.modelId),
    chunkCount: run?.chunkCount ?? 0,
    oversized: run?.oversized ?? 0,
    medianTokens: run?.medianTokens ?? 0,
    budget: state.settings.budget,
    topK: kFor(state.settings.budget, state.settings.size),
  });
  wrap.append(preview);
  return wrap;
}

function finishExport(rows: ChunkRow[], score: RunScore): void {
  state.busy = false;
  clearProgress();
  const model = modelById(state.settings.modelId);
  const ctx = {
    strategy: state.settings.strategy,
    size: state.settings.size,
    overlap: state.settings.overlap,
    model,
    chunkCount: score.chunkCount,
    oversized: score.oversized,
    medianTokens: score.medianTokens,
    budget: state.settings.budget,
    topK: score.k,
  };
  const h = headline();
  const files: Record<string, string> = {
    'chunks.jsonl': chunksJsonl(rows),
    'splitter-config.md': splitterConfig(ctx),
    'comparison.csv': comparisonCsv(state.sweep.length ? state.sweep : [score]),
    'report.html': reportHtml({
      ...ctx,
      headline: h.text,
      generatedAt: new Date().toISOString().slice(0, 16).replace('T', ' '),
      queries: state.queries.map((q) => {
        const s = score.perQuery.find((p) => p.queryId === q.id);
        return {
          text: q.text,
          marked: q.spans.length > 0,
          retrieved: s?.retrieved ?? false,
          rank: s?.firstHitRank ?? null,
          chunkCount: s?.chunkCount ?? score.chunkCount,
          retrievedTokens: s?.retrievedTokens ?? 0,
        };
      }),
      sweep: state.sweep.length ? state.sweep : [score],
      documents: state.docs.map((d) => ({
        name: d.name,
        tokens: docTokens.get(d.name) ?? 0,
        pages: d.pages.length,
      })),
    }),
    'README.txt':
      'chunkforge export\n\n' +
      'chunks.jsonl        one JSON object per chunk: id, source_file, page, char_start,\n' +
      '                    char_end, token_count, heading_prefix, text. The character span\n' +
      "                    indexes chunkforge's extracted text for that file, not the raw PDF\n" +
      '                    bytes; the chunk text itself is included so nothing depends on it.\n' +
      'splitter-config.md  the configuration that produced those chunks, with the caveats\n' +
      '                    that change what the numbers mean in each framework.\n' +
      'comparison.csv      every setting measured, including precision and IoU.\n' +
      'report.html         a self-contained summary. No scripts, no network, safe to attach.\n',
  };
  download(buildZip(files), 'chunkforge-run.zip');
  log.add(`Exported ${rows.length.toLocaleString()} chunks`, 'good');
  render();
}

/* ── overlays ─────────────────────────────────────────────────────────────── */

/**
 * Every overlay gets all four exits: the × control, a tap anywhere outside the
 * panel, Escape, and a real touch sequence on the backdrop. They are not
 * alternatives. An overlay you cannot dismiss makes the whole tool unusable, and
 * on a phone the control that opened it is underneath the overlay.
 *
 * `pointerdown` is used rather than `click` because it covers mouse, touch and
 * pen in one handler and fires where a synthetic click does not — and because
 * attaching both `click` and `touchstart` to the same control double-fires on a
 * real tap, closing and instantly reopening, which reads as a dead button.
 */
function mountOverlay(node: HTMLElement, panel: HTMLElement, onClose: () => void): () => void {
  const close = () => {
    cleanup();
    onClose();
  };
  const onPointer = (e: PointerEvent) => {
    if (!panel.contains(e.target as Node)) close();
  };
  const onKey = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      close();
    }
  };
  const cleanup = () => {
    node.removeEventListener('pointerdown', onPointer);
    document.removeEventListener('keydown', onKey, true);
    node.remove();
  };
  node.addEventListener('pointerdown', onPointer);
  document.addEventListener('keydown', onKey, true);
  return close;
}

function openModal(title: string, templateId: string): void {
  const tpl = document.getElementById(templateId) as HTMLTemplateElement | null;
  const backdrop = el('div', 'modal-backdrop');
  const modal = el('div', 'modal');
  modal.setAttribute('role', 'dialog');
  modal.setAttribute('aria-modal', 'true');
  modal.setAttribute('aria-label', title);

  const head = el('div', 'modal-head');
  head.append(el('h2', 'modal-title', title));
  const x = el('button', 'close-x', '✕');
  x.setAttribute('aria-label', 'Close');
  x.style.marginLeft = 'auto';
  head.append(x);

  const body = el('div', 'modal-body');
  if (tpl) body.append(tpl.content.cloneNode(true));

  modal.append(head, body);
  backdrop.append(modal);
  document.body.append(backdrop);

  const close = mountOverlay(backdrop, modal, () => {});
  x.addEventListener('pointerdown', (e) => {
    e.stopPropagation();
    close();
  });
  x.focus();
}

function openReader(query: QuerySpec): void {
  const backdrop = el('div', 'reader-backdrop');
  const reader = el('div', 'reader');
  reader.setAttribute('role', 'dialog');
  reader.setAttribute('aria-modal', 'true');
  reader.setAttribute('aria-label', 'Mark the passage that answers this query');

  const head = el('div', 'reader-head');
  head.append(el('span', 'reader-title', 'Click the passage that answers it'));
  head.append(el('span', 'step-note', 'or select any text — one mark scores every strategy'));
  const x = el('button', 'close-x', '✕');
  x.setAttribute('aria-label', 'Close');
  x.style.marginLeft = 'auto';
  head.append(x);

  const body = el('div', 'reader-body');
  for (let docId = 0; docId < state.docs.length; docId++) {
    const doc = state.docs[docId];
    if (state.docs.length > 1) {
      const name = el('div', 'rd-h', doc.name);
      body.append(name);
    }
    const blocks = [...doc.headings.map((h) => ({ ...h, kind: 'h' as const })), ...doc.paragraphs.map((p) => ({ ...p, kind: 'p' as const, level: 0, text: '' }))].sort(
      (a, b) => a.start - b.start,
    );
    for (const block of blocks) {
      const node = el('p', block.kind === 'h' ? 'rd-h' : 'rd-p', doc.text.slice(block.start, block.end));
      node.dataset.s = String(block.start);
      node.dataset.d = String(docId);
      const marked = query.spans.some((s) => s.docId === docId && s.start < block.end && s.end > block.start);
      if (marked) node.classList.add('is-marked');
      node.addEventListener('click', () => {
        const sel = window.getSelection();
        const text = sel?.toString() ?? '';
        if (text.trim().length > 3 && sel && sel.anchorNode && node.contains(sel.anchorNode)) {
          const base = Number(node.dataset.s);
          const range = sel.getRangeAt(0);
          const start = base + range.startOffset;
          const end = base + range.endOffset;
          query.spans = [{ docId, start: Math.min(start, end), end: Math.max(start, end) }];
        } else {
          query.spans = [{ docId, start: block.start, end: block.end }];
        }
        log.add(`Marked a ${query.spans[0].end - query.spans[0].start}-character passage as the answer`, 'good');
        closeReader();
        requestRun();
        render();
      });
      body.append(node);
    }
  }

  const foot = el('div', 'reader-head');
  const clear = el('button', 'btn', 'Clear the mark');
  clear.addEventListener('click', () => {
    query.spans = [];
    closeReader();
    requestRun();
    render();
  });
  foot.append(clear);

  reader.append(head, body, foot);
  backdrop.append(reader);
  document.body.append(backdrop);

  const closeReader = mountOverlay(backdrop, reader, () => {});
  x.addEventListener('pointerdown', (e) => {
    e.stopPropagation();
    closeReader();
  });

  const firstMarked = body.querySelector('.is-marked');
  if (firstMarked) firstMarked.scrollIntoView({ block: 'center' });
}

function logToggle(): HTMLElement {
  const btn = el('button', 'btn log-toggle', 'Event log');
  btn.addEventListener('click', () => openLog());
  return btn;
}

function openLog(): void {
  const backdrop = el('div', 'log-backdrop');
  const drawer = el('aside', 'log-drawer');
  drawer.setAttribute('role', 'dialog');
  drawer.setAttribute('aria-label', 'Event log');

  const head = el('div', 'log-head');
  head.append(el('strong', undefined, 'Event log'));
  const copy = el('button', 'btn', 'Copy');
  copy.addEventListener('click', () => void navigator.clipboard?.writeText(log.toText()));
  const x = el('button', 'close-x', '✕');
  x.setAttribute('aria-label', 'Close the event log');
  x.style.marginLeft = 'auto';
  head.append(x, copy);

  const list = el('div', 'log-list');
  const paint = (entries: readonly LogEntry[]) => {
    list.replaceChildren();
    for (const e of entries) {
      const line = el('div', `log-line${e.kind === 'info' ? '' : ` is-${e.kind}`}`);
      line.append(el('span', 'log-time', `[${stamp(e.at)}] `), document.createTextNode(e.message));
      list.append(line);
    }
    list.scrollTop = list.scrollHeight;
  };
  const unsubscribe = log.subscribe(paint);

  drawer.append(head, list);
  backdrop.append(drawer);
  document.body.append(backdrop);

  const close = mountOverlay(backdrop, drawer, unsubscribe);
  x.addEventListener('pointerdown', (e) => {
    e.stopPropagation();
    close();
  });
}

/* ── boot ─────────────────────────────────────────────────────────────────── */

mountGlossary(app);
render();

log.add('chunkforge ready — nothing has been uploaded and nothing will be');
setProgress('loading the scoring model', 0, false);
worker.postMessage({ t: 'load', modelId: state.settings.modelId });

document.addEventListener('keydown', (event) => {
  if (event.key === '?' && !(event.target instanceof HTMLInputElement)) {
    openModal('How chunkforge works', 'tpl-how');
  }
});

window.addEventListener('paste', (event) => {
  const text = event.clipboardData?.getData('text/plain');
  if (!text || text.length < 200) return;
  if (document.activeElement instanceof HTMLInputElement) return;
  event.preventDefault();
  const file = new File([text], `pasted-${state.docs.length + 1}.txt`, { type: 'text/plain' });
  void ingest([file]);
});
